import { pipe } from 'fp-ts/function';
import { record, array } from 'fp-ts';
import { Ord } from 'fp-ts/string';
import { filter, flatten, map } from 'fp-ts/Array';
import {
    EnterpriseBuilderDependencyMappingsGetResult,
    EnterpriseBuilderParameters,
    EnterpriseBuilderParametersType,
    SerializedDependencyDetail,
    EntitySelection,
} from '@mews-commander/data-access';
import { FieldAdapter, lt } from '@mews/framework';
import { upperFirst } from '@mews/std';
import { values } from '@mews/fp';
import { createFlagEnum } from '@mews-commander/legacy-form';
import { Option, TreeOption } from '@mews/b2b-ui';
import { EnterpriseBuilderProFormValues } from './components/EnterpriseBuilderProForm/formMapping';

export const EntitySelectionFlagEnum = createFlagEnum('EntitySelection')<keyof EntitySelection, ''>({
    accountingCategories: 'AccountingCategories',
    departments: 'Departments',
    mailTemplates: 'MailTemplates',
    accountingConfiguration: 'AccountingConfiguration',
    bookableServices: 'BookableServices',
    bookableProductCategories: 'BookableProductCategories',
    bookableProducts: 'BookableProducts',
    bookableSegments: 'BookableSegments',
    additionalServices: 'AdditionalServices',
    additionalProductCategories: 'AdditionalProductCategories',
    additionalProducts: 'AdditionalProducts',
    additionalSegments: 'AdditionalSegments',
    spaceCategories: 'SpaceCategories',
    spaces: 'Spaces',
    rateGroups: 'RateGroups',
    rates: 'Rates',
    spaceFeatures: 'SpaceFeatures',
    productRules: 'ProductRules',
    ruleActions: 'RuleActions',
});

export const createEnterpriseBuilderParameters = (
    formData: EnterpriseBuilderProFormValues,
    propertyType: EnterpriseBuilderParametersType
): EnterpriseBuilderParameters => {
    if (propertyType === EnterpriseBuilderParametersType.NewEnterprise) {
        return {
            data: {
                discriminator: EnterpriseBuilderParametersType.NewEnterprise,
                value: {
                    enterpriseId: formData.enterpriseId,
                    uniqueName: formData.uniqueName,
                    subscriptionId: formData.subscriptionId,
                    chainId: formData.chainId,
                    cityId: formData.cityId,
                    selectedEntities: EntitySelectionFlagEnum.mapArrayToFlags(formData.exportSettings),
                },
            },
        };
    }

    return {
        data: {
            discriminator: EnterpriseBuilderParametersType.ExistingEnterprise,
            value: {
                enterpriseReferenceMapping: {
                    sourceEntityId: formData.enterpriseId,
                    targetEntityId: formData.propertyIdentifier,
                },
                selectedEntities: EntitySelectionFlagEnum.mapArrayToFlags(formData.exportSettings),
            },
        },
    };
};

const createNestedOptions = (
    optionsObject: {
        [p: string]: SerializedDependencyDetail;
    },
    copyToExisting: boolean
): Option[] =>
    pipe(
        optionsObject,
        record.collect(Ord)((key) => ({
            label: lt(upperFirst(key) as Mews.TextKey),
            id: key,
            value: key,
            tagProps:
                key === 'Spaces' && copyToExisting
                    ? { variant: 'basicSubtle', children: lt('GeneralOverwrite') }
                    : undefined,
        }))
    );

export const transformDependencies = (
    dependencyMap: EnterpriseBuilderDependencyMappingsGetResult,
    copyToExisting: boolean
): TreeOption[] =>
    pipe(
        dependencyMap,
        record.collect(Ord)((key, value) => ({
            label: lt(upperFirst(key) as Mews.TextKey),
            id: key,
            options: createNestedOptions(value, copyToExisting),
        }))
    );

const checkDepenedencies = (
    componentValues: string[] | string | null,
    data: EnterpriseBuilderDependencyMappingsGetResult,
    selectedEntities: string[]
) => {
    if (!Array.isArray(componentValues)) return null;

    let finalSelection: string[] = [...componentValues];

    const appendDependsOn = (acc: string[], dependencyDetail: SerializedDependencyDetail) =>
        dependencyDetail.dependsOn?.length ? acc.concat(dependencyDetail.dependsOn) : acc;

    finalSelection = pipe(
        values(data),
        map((item) => Object.entries(item)),
        flatten,
        filter(([key]) => componentValues.includes(key)),
        map(([, value]) => value),
        array.reduce(finalSelection, appendDependsOn)
    );

    if (componentValues.length < selectedEntities.length && componentValues.length) {
        const difference = selectedEntities.filter((item) => !componentValues.includes(item));

        const excludeDependants = (acc: string[], dependencyDetail: SerializedDependencyDetail): string[] =>
            dependencyDetail.dependants?.length
                ? selectedEntities.filter((item) => !dependencyDetail.dependants?.includes(item))
                : acc;

        finalSelection = pipe(
            values(data),
            map((item) => Object.entries(item)),
            flatten,
            filter(([key]) => difference.includes(key)),
            map(([, value]) => value),
            array.reduce(finalSelection, excludeDependants),
            array.filter((item) => !difference.includes(item))
        );
    }

    return [...new Set(finalSelection)];
};

export const createAdapter = (data: EnterpriseBuilderDependencyMappingsGetResult, selectedEntities: string[]) =>
    ({
        input: (componentValues) => checkDepenedencies(componentValues, data, selectedEntities),
    }) satisfies FieldAdapter<string[] | string | null>;
